import { PersonOutline } from '@mui/icons-material';
import { Divider, Grow, Paper, Popper, styled, Typography as TypographyMUI, useTheme } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import useTranslation, { arrayOfSupportedLanguages } from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { useNavigate } from 'react-router';
import Icon from 'ui/atoms/Icon/Icon';
import Separator from 'ui/atoms/Separator/Separator';
import Typography from 'ui/atoms/Typography/Typography';
import Profile, { Props as ProfileProps } from 'ui/molecules/Profile/Profile';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

const Wrapper = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.primary.main}`,
  width: '100vw',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.3125rem 1.5rem'
}));

export interface Props {
  profile: ProfileProps;
  isMenuOpen: boolean;
  isMobileDeviceRegistered: boolean;
  anchorRef: React.RefObject<HTMLDivElement>;
  popperRef: React.RefObject<HTMLDivElement>;
  languageSelectValue: string;
  handleLanguageSelectOnChange: (event: SelectChangeEvent) => void;
  handleOpenMenu: () => void;
  handleMyProfileOnClick: () => void;
  handleLogoutOnClick: () => void;
}

const TopBar: React.FC<React.PropsWithChildren<Props>> = ({
  profile,
  isMenuOpen,
  isMobileDeviceRegistered,
  anchorRef,
  popperRef,
  handleOpenMenu,
  languageSelectValue,
  handleLanguageSelectOnChange,
  handleMyProfileOnClick,
  handleLogoutOnClick
}) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const navigate = useNavigate();

  return (
    <Wrapper id="topbar">
      <ComponentWrapper flex="1" alignSelf="flex-start" gap="0.5rem" alignItems="center" justifyContent="start">
        <ComponentWrapper onClick={() => navigate('/')} id="logo-title" cursor="pointer" gap="0.5rem">
          <Icon name="ArrowRight" width={15} height={24} viewBox="0 0 24 24" color="primary" />{' '}
          <TypographyMUI id="title" variant="h5" color="primary">
            ATLAS
          </TypographyMUI>
        </ComponentWrapper>
      </ComponentWrapper>
      <ComponentWrapper flex="1 0 4rem" justifyContent="end" gap="1rem">
        <Divider variant="fullWidth" orientation="vertical" style={{ height: '2rem' }} />
        <Profile
          name={profile.name}
          picture={profile.picture}
          anchorRef={anchorRef}
          onClick={handleOpenMenu}
          hoverEffect={!!handleOpenMenu}
        />
        <Popper
          open={isMenuOpen}
          role={undefined}
          placement="bottom-start"
          ref={popperRef}
          transition
          disablePortal
          style={{ zIndex: 1000, backgroundColor: `${theme.palette.background.paper} !important` }}
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                position: 'fixed',
                zIndex: 1000,
                top: '3rem',
                right: '2.75rem',
                width: '15rem',
                padding: '0.625rem 1rem',
                transformOrigin: 'right-top',
                backgroundColor: `${theme.palette.background.paper} !important`
              }}
            >
              <Paper
                style={{
                  backgroundColor: `${theme.palette.background.paper} !important`
                }}
              >
                <MenuList id="menu-id" autoFocusItem={isMenuOpen} aria-labelledby="composition-button">
                  <FormControl fullWidth>
                    <ComponentWrapper flexDirection="column" gap="0.25rem">
                      <Typography variant="label" language-select-label>
                        {translation.language}
                      </Typography>
                      <Select id="language-select" value={languageSelectValue} onChange={handleLanguageSelectOnChange}>
                        {arrayOfSupportedLanguages.map((supportedLanguage) => (
                          <MenuItem value={supportedLanguage.value}>{supportedLanguage.label}</MenuItem>
                        ))}
                      </Select>
                    </ComponentWrapper>
                  </FormControl>
                  <Separator marginTop="0.625rem" />
                  <MenuItem onClick={handleMyProfileOnClick}>
                    <ComponentWrapper gap="1rem" height="1.625rem" margin="0.25rem 0">
                      <PersonOutline />
                      {translation.my_profile}
                    </ComponentWrapper>
                  </MenuItem>
                  <MenuItem onClick={handleLogoutOnClick}>
                    <ComponentWrapper gap="1rem" height="1.625rem">
                      <Icon name="LogOut" />
                      {translation.logout}
                    </ComponentWrapper>
                  </MenuItem>
                </MenuList>
              </Paper>
            </Grow>
          )}
        </Popper>
      </ComponentWrapper>
    </Wrapper>
  );
};

export default TopBar;
